import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { isArray, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { ListTransfer } from '~/components';
import { Input, Select } from '~/components/Form/';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function FormProfile({
  sending,
  close,
  active,
  report,
  toggleDisabled,
}) {
  const [campos, setCampos] = useState([]);

  const formRef = useRef(null);

  const { client, id } = useSelector((state) => state.user.profile);

  const optionsSN = useMemo(
    () => [
      { label: t('s.geral.opcoes.nao'), value: 'N' },
      { label: t('s.geral.opcoes.sim'), value: 'S' },
    ],
    []
  );

  const cells = useMemo(
    () => [
      {
        id: 'codigolabelsistema',
        label: t('s.geral.descricaocampo'),
      },
    ],
    []
  );

  const start = useMemo(() => {
    if (active.campos)
      return active.campos.map((value) => ({
        ...value,
        codigolabelsistema: t(value.codigolabelsistema),
      }));

    return [];
  }, [active]);

  const handleSerialize = useCallback(
    (values) =>
      values
        .map((value) => ({
          ...value,
          codigolabelsistema: t(value.codigolabelsistema),
        }))
        .sort((prev, post) =>
          prev.codigolabelsistema < post.codigolabelsistema ? -1 : 1
        ),
    []
  );

  const handleSubmit = useCallback(
    async (value) => {
      const errors = [];

      try {
        toggleDisabled(true);

        const schema = Yup.object().shape({
          nomeperfilrelatorio: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50)),
          privadoperfilrelatorio: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await schema.validate(value, {
          abortEarly: false,
        });

        if (isEmpty(campos)) errors.push('Selecione ao menos um registro!');
        if (!isEmpty(errors)) throw errors;

        const formData = {
          ...active,
          ...value,
          idrelatorio: report,
          idcliente: client,
          idusuario: id,
          campos,
        };

        delete formData.perfilpadrao;

        await api({
          url: '/perfisrelatorios',
          method: isEmpty(active) ? 'POST' : 'PUT',
          data: formData,
        });

        toast.success(
          t(
            isEmpty(active)
              ? 's.geral.sucessoinclusao'
              : 's.geral.sucessoalteracao'
          )
        );
        close();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        } else if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        toggleDisabled(false);
      }
    },
    [campos, active, report, client, id]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <S.WrapperForm ref={formRef} initialData={active} onSubmit={handleSubmit}>
      <Input name="nomeperfilrelatorio" label={t('s.geral.descricaoperfil')} />

      <Select
        name="privadoperfilrelatorio"
        label={t('s.geral.privado')}
        options={optionsSN}
      />

      <ListTransfer
        search
        grid
        move
        url={`/perfisrelatorios/${report}/${active.idperfilrelatorio || 0}`}
        unique="camporelatoriocampo"
        label="codigolabelsistema"
        leftTitle={t('s.geral.disponiveis')}
        rightTitle={t('s.geral.selecionados')}
        maxWidth="39"
        maxHeight="180"
        paginate={999}
        tree={false}
        showPaginate={false}
        cells={cells}
        formatter={handleSerialize}
        onCallback={(values) => setCampos(values)}
        start={start}
      />
    </S.WrapperForm>
  );
}

FormProfile.propTypes = {
  sending: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  active: PropTypes.objectOf(PropTypes.any),
  report: PropTypes.number.isRequired,
  toggleDisabled: PropTypes.bool.isRequired,
};

FormProfile.defaultProps = {
  active: {},
};
